import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { isFutureDate } from '../../../utils/dateUtils';

const ContactModal = ({ isOpen, onClose, contact, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    appointmentDate: '',
    salesRep: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact?.name || '',
        email: contact?.email || '',
        companyName: contact?.companyName || '',
        appointmentDate: contact?.appointmentDate || '',
        salesRep: contact?.salesRep || ''
      });
      setErrors({});
    } else {
      setFormData({
        name: '',
        email: '',
        companyName: '',
        appointmentDate: '',
        salesRep: ''
      });
      setErrors({});
    }
  }, [contact, isOpen]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData?.name?.trim()?.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData?.appointmentDate) {
      newErrors.appointmentDate = 'Appointment date is required';
    } else {
      // Validate that appointment date is in the future (Asia/Calcutta timezone)
      if (!isFutureDate(formData?.appointmentDate)) {
        newErrors.appointmentDate = 'Appointment date must be in the future';
      }
    }

    if (!formData?.salesRep?.trim()) {
      newErrors.salesRep = 'Sales representative name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
      handleClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      companyName: '',
      appointmentDate: '',
      salesRep: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={contact ? 'Edit Contact' : 'Add New Contact'}
      footer={
        <>
          <div className="flex-1" />
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleSubmit}>
            {contact ? 'Save Changes' : 'Add Contact'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
        <Input
          label="Name"
          type="text"
          placeholder="Enter contact name"
          value={formData?.name}
          onChange={(e) => handleChange('name', e?.target?.value)}
          error={errors?.name}
          required
        />

        <Input
          label="Email"
          type="email"
          placeholder="Enter email address"
          value={formData?.email}
          onChange={(e) => handleChange('email', e?.target?.value)}
          error={errors?.email}
          description="We'll use this to send appointment reminders"
          required
        />

        <Input
          label="Company Name"
          type="text"
          placeholder="Enter company name"
          value={formData?.companyName}
          onChange={(e) => handleChange('companyName', e?.target?.value)}
          error={errors?.companyName}
          required
        />

        <Input
          label="Appointment Date"
          type="date"
          value={formData?.appointmentDate}
          onChange={(e) => handleChange('appointmentDate', e?.target?.value)}
          error={errors?.appointmentDate}
          description="Select the scheduled appointment date"
          required
        />

        <Input
          label="Sales Representative"
          type="text"
          placeholder="Enter sales rep name"
          value={formData?.salesRep}
          onChange={(e) => handleChange('salesRep', e?.target?.value)}
          error={errors?.salesRep}
          description="Assign a sales representative to this contact"
          required
        />
      </form>
    </Modal>
  );
};

export default ContactModal;